/**
 * Client-side group photos API module — replaces group-photos/actions.ts Server Actions.
 */

import { GROUP_PHOTOS } from "@/shared/constants/backend-api"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { buildUrl } from "@/shared/lib/utils"

export interface GroupPhoto {
  id: number
  groupId: string
  uploadedByUserId?: number | null
  uploadedByUser?: { id: number; fullname: string | null } | null
  imageUrl: string
  thumbnailUrl: string
  mediaType: 'image' | 'video' | 'document'
  takenAt: string | null
  uploadedAt: string
  description?: string | null
  tags?: string[] | null
  latitude?: number | null
  longitude?: number | null
  duration?: number | null
  videoCodec?: string | null
  videoResolution?: string | null
  fileSize?: number | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface GroupPhotoFilterOptions {
  mediaType?: 'image' | 'video' | 'document'
  includeDeleted?: boolean
  dateFrom?: string
  dateTo?: string
  /** e.g. ['Confidential'] to show only confidential photos */
  tags?: string[]
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const PHOTO_QUERY_KEYS = {
  all: ["group-photos"] as const,
  byGroup: (groupId: string) => ["group-photos", "group", groupId] as const,
  detail: (photoId: number) => ["group-photos", "detail", photoId] as const,
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export async function getPhotosByGroupId(
  groupId: string,
  filters?: GroupPhotoFilterOptions
): Promise<ApiResponse<GroupPhoto[]>> {
  const params: Record<string, string> = {}
  if (filters?.mediaType) params.mediaType = filters.mediaType
  if (filters?.includeDeleted) params.includeDeleted = 'true'
  if (filters?.dateFrom) params.dateFrom = filters.dateFrom
  if (filters?.dateTo) params.dateTo = filters.dateTo
  if (filters?.tags?.length) params.tags = filters.tags.join(',')

  const url = buildUrl(GROUP_PHOTOS.byGroup(groupId), params)
  return authFetchJson<GroupPhoto[]>(url)
}

export async function getGroupPhotoById(photoId: number): Promise<ApiResponse<GroupPhoto>> {
  return authFetchJson<GroupPhoto>(GROUP_PHOTOS.byId(photoId))
}

export async function updateGroupPhoto(
  photoId: number,
  data: { description?: string; takenAt?: string; tags?: string[] }
): Promise<ApiResponse<GroupPhoto>> {
  return authFetchJson<GroupPhoto>(GROUP_PHOTOS.byId(photoId), {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function deleteGroupPhoto(photoId: number): Promise<ApiResponse<string>> {
  return authFetchJson<string>(GROUP_PHOTOS.delete(photoId), {
    method: "DELETE",
  })
}

export async function permanentlyDeleteGroupPhoto(photoId: number): Promise<ApiResponse<string>> {
  return authFetchJson<string>(GROUP_PHOTOS.deletePermanent(photoId), {
    method: "DELETE",
  })
}
